import { useState, useEffect } from 'react';
import { supabase } from '../supabase';
import type { Database } from '../database.types';
import { containsCreditCardNumber } from '../validation';
import OpenAI from 'openai';

let openai: OpenAI | null = null;
try {
  openai = new OpenAI({
    apiKey: import.meta.env.VITE_OPENAI_API_KEY || '',
    dangerouslyAllowBrowser: true
  });
} catch (err) {
  console.error('Failed to initialize OpenAI client:', err);
}

export function useCustomerNotes(customerId: string) {
  const [notes, setNotes] = useState<Database['public']['Tables']['customer_notes']['Row'][]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function fetchNotes() {
      if (!customerId) {
        setNotes([]);
        setLoading(false);
        return;
      }

      try {
        const { data, error: fetchError } = await supabase
          .from('customer_notes')
          .select('*')
          .eq('customer_id', customerId)
          .order('created_at', { ascending: false });

        if (fetchError) throw fetchError;
        setNotes(data || []);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to fetch notes');
      } finally {
        setLoading(false);
      }
    }

    fetchNotes();
  }, [customerId]);

  const validateNoteContent = async (content: string): Promise<void> => {
    // First check for credit card numbers
    if (containsCreditCardNumber(content)) {
      throw new Error('Credit card numbers cannot be stored in notes for security reasons.');
    }

    // Use OpenAI for additional validation if available
    if (openai) {
      try {
        const completion = await openai.chat.completions.create({
          messages: [{ 
            role: "system", 
            content: `You are a security validator. Analyze if this text contains sensitive payment information like credit card numbers, bank account numbers, or security codes. Regular payment amounts (e.g. "$500 due") are allowed. Respond with only "SAFE" or "UNSAFE". Text to analyze: "${content}"`
          }],
          model: "gpt-3.5-turbo",
          temperature: 0,
          max_tokens: 1
        });

        const response = completion.choices[0]?.message?.content?.trim().toUpperCase();
        if (response !== 'SAFE') {
          throw new Error('This note may contain sensitive information that cannot be stored for security reasons.');
        }
      } catch (err) {
        console.warn('OpenAI validation failed, using basic validation only:', err);
      }
    }
  };

  const addNote = async (content: string) => {
    try {
      // Validate note content
      await validateNoteContent(content);

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data, error } = await supabase
        .from('customer_notes')
        .insert([{
          customer_id: customerId,
          content,
          created_by: user.id
        }])
        .select()
        .single();

      if (error) throw error;
      setNotes(prev => [data, ...prev]);
      return data;
    } catch (err) {
      if (err instanceof Error) {
        // Enhance error message for better user understanding
        const errorMessage = err.message.includes('security reasons')
          ? err.message
          : 'Failed to add note. Please ensure no sensitive information is included.';
        throw new Error(errorMessage);
      }
      throw err;
    }
  };

  const deleteNote = async (noteId: string) => {
    try {
      const { error } = await supabase
        .from('customer_notes')
        .delete()
        .eq('id', noteId);

      if (error) throw error;
      setNotes(prev => prev.filter(note => note.id !== noteId));
    } catch (err) {
      throw err;
    }
  };

  return { notes, loading, error, addNote, deleteNote };
}